import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SetupGuide from "@/components/SetupGuide";
import {
  BaseSidebarWrapper,
  SidebarItem,
} from "@/components/Common/HeaderSidebar/HeaderSidebar";
import { useCopilotEnabled } from "@/hooks/useCopilotEnabled";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import {
  useOnboardingProgress,
  useSetSidebarCollapsed,
} from "@/hooks/useOnboardingProgress";
import { getUserId } from "@/helpers/common";
import { pageURLs } from "@/constants";
import Client from "@/images/v2/Client-icon.svg";
import Home from "@/images/v2/Home-icon.svg";
import Inventory from "@/images/v2/Inventory.svg";
import Wantlisting from "@/images/v2/Wantlisting.svg";
import CopilotIcon from "@/images/v2/genie-mark.svg";
import "./BlsSidebar.scss";

const SIDEBAR_PAGE = 1004;

const NUDGE_MS = 320;

const nudgeLayout = () => {
  const start = performance.now();
  let frame;
  const tick = (now) => {
    window.dispatchEvent(new Event("resize"));
    if (now - start < NUDGE_MS) frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
};

const BlsSidebar = ({
  currentURL = "",
  shouldToggleColor,
  handleGoFeed,
  handleGoWantListings,
  handleGoClients,
  handleGoInventory,
  handleGoChat,
}) => {
  const copilotEnabled = useCopilotEnabled();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  // Same gate as SetupGuide/MemberTour: the default Gitcha tenant has no
  // onboarding progress to load, so don't fetch it on every page and focus.
  const signedIn = Boolean(getUserId()) && !isGitchaDefaultWebsite;
  const { data: progress } = useOnboardingProgress(signedIn);
  const saveCollapsed = useSetSidebarCollapsed();
  const [localCollapsed, setLocalCollapsed] = useState(null);
  const collapsed = localCollapsed ?? Boolean(progress?.sidebarCollapsed);
  const [animated, setAnimated] = useState(false);
  const [tip, setTip] = useState(null);
  const firstRender = useRef(true);

  useEffect(() => {
    document.body.classList.add("has-bls-sidebar");
    return () =>
      document.body.classList.remove(
        "has-bls-sidebar",
        "bls-sidebar-collapsed",
      );
  }, []);

  useEffect(() => {
    document.body.classList.toggle("bls-sidebar-collapsed", collapsed);
    setTip(null);
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }
    return nudgeLayout();
  }, [collapsed]);

  useEffect(() => {
    if (animated || (signedIn && !progress)) return undefined;
    const frame = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [animated, signedIn, progress]);

  const showTip = (event) => {
    const item = event.target.closest?.(".sidebar-wrapper, [data-tip]");
    if (!item || (!collapsed && !item.dataset.tip)) return setTip(null);
    const label =
      item.dataset.tip ||
      item
        .querySelector(".sidebar-menu-list")
        ?.textContent.replace(/\s+/g, " ")
        .trim();
    const icon = (
      item.querySelector(".sidebar-menu") || item
    ).getBoundingClientRect();
    if (label)
      setTip({ label, top: icon.top + icon.height / 2, left: icon.right + 12 });
  };

  const hideTip = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    )
      return;
    setTip(null);
  };

  const toggle = () => {
    const next = !collapsed;
    setLocalCollapsed(next);
    if (signedIn && progress) {
      saveCollapsed.mutate(
        {
          page: progress.pages?.sidebar || SIDEBAR_PAGE,
          collapsed: next,
        },
        // The mutation rolls the cache back on failure; drop the local
        // override so the rail follows it instead of staying out of sync.
        { onError: () => setLocalCollapsed(null) },
      );
    }
  };

  return (
    <BaseSidebarWrapper
      shouldToggleColor={shouldToggleColor}
      className={`bls-sidebar${collapsed ? " is-collapsed" : ""}${animated ? " is-animated" : ""}`}
    >
      <div
        className="bls-sidebar__items"
        onMouseOver={showTip}
        onFocus={showTip}
        onMouseLeave={hideTip}
        onBlur={hideTip}
      >
        <SidebarItem
          label="Feed"
          icon={Home}
          onClick={handleGoFeed}
          isActive={currentURL === "/" || currentURL === pageURLs.home}
          tourId="nav-feed"
        />
        <SidebarItem
          label={
            <>
              My <br /> Want-Listings
            </>
          }
          icon={Wantlisting}
          isActive={currentURL === pageURLs.agentMyWantAdListings}
          onClick={handleGoWantListings}
          tourId="nav-want-listings"
        />
        <SidebarItem
          label="My Properties"
          icon={Inventory}
          isActive={currentURL === pageURLs.agentInventoryListing}
          onClick={handleGoInventory}
          tourId="nav-properties"
        />
        <SidebarItem
          label="My Clients"
          icon={Client}
          onClick={handleGoClients}
          isActive={currentURL === pageURLs.agentMyClients}
        />
        {copilotEnabled ? (
          <SidebarItem
            label="Genie"
            icon={CopilotIcon}
            onClick={handleGoChat}
            isActive={currentURL.startsWith(pageURLs.agentChat)}
            tourId="genie-ai"
          />
        ) : null}
      </div>

      <div
        className="bls-sidebar__foot"
        onMouseOver={showTip}
        onFocus={showTip}
        onMouseLeave={hideTip}
        onBlur={hideTip}
      >
        <SetupGuide placement="sidebar" collapsed={collapsed} />
        <button
          type="button"
          className="bls-sidebar__toggle"
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          data-tip={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <rect
              x="3.5"
              y="4.5"
              width="17"
              height="15"
              rx="3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            />
            <path
              d="M9.5 4.5v15"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            />
          </svg>
        </button>
      </div>
      {tip
        ? createPortal(
            <span
              className="bls-sidebar-tip"
              role="tooltip"
              style={{ top: tip.top, left: tip.left }}
            >
              {tip.label}
            </span>,
            document.body,
          )
        : null}
    </BaseSidebarWrapper>
  );
};

export default BlsSidebar;
